/**
 * Chat WebSocket Hook for Web Dashboard
 * Refactored from Socket.io to Native WebSocket for AWS API Gateway compatibility
 *
 * FEATURE FLAG: Controlled by REACT_APP_CHAT_ENABLED in .env
 * Set REACT_APP_CHAT_ENABLED=true to enable WebSocket connections
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { SOCKETIO_URL, CHAT_ENABLED, WS_PATH } from '../config/websocket';
import { WebSocketService, WebSocketMessage, WebSocketStatus } from '../services/websocketService';
import { wsLogger } from '../utils/logger';

interface ChatWebSocketOptions {
  conversationId?: string;
  onTypingStart?: (userId: string) => void;
  onTypingEnd?: (userId: string) => void;
  onReadReceipt?: (messageIds: string[], userId: string) => void;
  onNewMessage?: (message: any) => void;
}

export function useChatWebSocket(options: ChatWebSocketOptions = {}) {
  const queryClient = useQueryClient();
  const { user, token } = useAuth();
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const [isConnected, setIsConnected] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const wsRef = useRef<WebSocketService | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use ref for options to avoid stale closures in WebSocket callbacks
  // Since the WebSocket service is not recreated when options change
  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  /**
   * Handle incoming WebSocket messages
   */
  const handleMessage = useCallback((message: WebSocketMessage) => {
    wsLogger.debug('Received message:', message);
    const currentOptions = optionsRef.current;

    switch (message.action) {
      case 'newMessage':
      case 'message_received':
      case 'newMessageNotification': // Handle notification for new messages when not in room
        wsLogger.debug('New message received (or notification)');

        // Stop typing indicator when message received
        setIsUserTyping(false);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        // Invalidate conversations list to fetch latest
        queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });

        // Refetch unread count
        queryClient.invalidateQueries({ queryKey: ['chat-unread-count'] });

        // If currently viewing messages for this conversation, update immediately
        const newMessageData = message.data || message.message;

        // Check if this is a notification or full message
        // If it's a notification, it might be { conversationId, messagePreview, userId }
        // If it's a full message, it has { message: { ... } }

        // For newMessageNotification, we might not have the full message to display in the chat window
        // But we should still invalidate queries.

        if ((message.action === 'newMessage' || message.action === 'message_received') && newMessageData && currentOptions.conversationId) {
          queryClient.invalidateQueries({
            queryKey: ['chat-messages', currentOptions.conversationId],
          });
          currentOptions.onNewMessage?.(newMessageData);

          // AUTO-MARK READ: If we are in this conversation, mark it as read immediately
          // This ensures the unread badge doesn't flash or stick
          if (currentOptions.conversationId && newMessageData?.conversationId === currentOptions.conversationId) {
            if (wsRef.current?.isConnected()) {
              // Mark as read immediately on the socket
              wsRef.current.send({
                action: 'markAsRead',
                data: { conversationId: currentOptions.conversationId }
              });
              // Optimistically update local cache if needed, but the invalidate above handles it eventually
            }
          }
        }
        break;

      case 'unread_count_update':
        // Support both formats: message.unread_count and message.data.unread_count
        const unreadCount = message.unread_count ?? message.data?.unread_count;
        wsLogger.debug('Unread count update:', unreadCount);

        if (unreadCount !== undefined) {
          // Update React Query cache immediately
          queryClient.setQueryData(['chat-unread-count'], unreadCount);

          // Also invalidate conversations to update per-conversation counts
          queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
        }
        break;

      case 'messageSent':
        wsLogger.debug('Message sent confirmation:', message.messageId);
        break;

      case 'typing':
        // User is typing
        const typingUserId = message.data?.userId || message.userId;
        const isTyping = message.data?.isTyping ?? true;

        if (isTyping) {
          setIsUserTyping(true);
          currentOptions.onTypingStart?.(typingUserId);

          // Auto-clear after 3 seconds if no stop event
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => {
            setIsUserTyping(false);
            currentOptions.onTypingEnd?.(typingUserId);
          }, 3000);
        } else {
          setIsUserTyping(false);
          currentOptions.onTypingEnd?.(typingUserId);
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
        }
        break;

      case 'readReceipt':
      case 'messagesRead':
      case 'messagesMarkedRead': // Backend sends this to sender
      case 'messageRead': // Backend sends this event
        const readMessageIds = message.data?.messageIds || message.messageIds || [];
        const readByUserId = message.data?.userId || message.userId;
        const conversationId = message.data?.conversationId || message.conversationId;

        wsLogger.debug('Read receipt:', readMessageIds, 'by', readByUserId);

        // Invalidate unread count since messages were read
        queryClient.invalidateQueries({ queryKey: ['chat-unread-count'] });
        queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });

        if (conversationId) {
          queryClient.invalidateQueries({ queryKey: ['chat-messages', conversationId] });
        }

        currentOptions.onReadReceipt?.(readMessageIds, readByUserId);
        break;

      case 'userOnline':
        const onlineUserId = message.data?.userId || message.userId;
        if (onlineUserId) {
          wsLogger.debug('User online:', onlineUserId);
          setOnlineUsers(prev => new Set(prev).add(onlineUserId));
        }
        break;

      case 'userOffline':
        const offlineUserId = message.data?.userId || message.userId;
        if (offlineUserId) {
          wsLogger.debug('User offline:', offlineUserId);
          setOnlineUsers(prev => {
            const next = new Set(prev);
            next.delete(offlineUserId);
            return next;
          });
        }
        break;

      case 'error':
        wsLogger.error('[WebSocket] Server error:', message.error);
        break;

      default:
        wsLogger.log('Unknown message action:', message.action);
    }
  }, [queryClient]);

  /**
   * Connect to WebSocket
   */
  const connect = useCallback(() => {
    // Skip connection if chat feature is disabled
    if (!CHAT_ENABLED) {
      wsLogger.log('Chat feature is disabled (REACT_APP_CHAT_ENABLED=false)');
      return;
    }

    if (!user || !token) {
      return;
    }

    if (wsRef.current?.isConnected()) {
      wsLogger.debug('Already connected');
      return;
    }

    // Convert HTTP/HTTPS URL to WebSocket URL and append /ws/chat path
    const wsUrl = SOCKETIO_URL
      .replace('http://', 'ws://')
      .replace('https://', 'wss://') + WS_PATH;

    wsLogger.log('Connecting to:', wsUrl);

    // Initialize WebSocket service
    wsRef.current = new WebSocketService(
      {
        url: wsUrl,
        token,
        reconnectInterval: 3000,
        maxReconnectAttempts: 5,
        heartbeatInterval: 30000,
        debug: process.env.NODE_ENV === 'development',
      },
      {
        onOpen: () => {
          wsLogger.info('✅ Connected');
          setIsConnected(true);
        },
        onClose: () => {
          wsLogger.info('🔌 Disconnected');
          setIsConnected(false);
        },
        onError: (error) => {
          wsLogger.error('[WebSocket] ❌ Error:', error);
        },
        onMessage: handleMessage,
        onStatusChange: (newStatus) => {
          setStatus(newStatus);
        },
      }
    );

    // Connect
    wsRef.current.connect();
  }, [user, token, handleMessage]);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsLogger.log('Disconnecting...');
      wsRef.current.disconnect();
      wsRef.current = null;
    }
  }, []);

  /**
   * Send typing indicator
   */
  const sendTyping = useCallback((conversationId: string, isTyping: boolean) => {
    if (wsRef.current?.isConnected()) {
      wsRef.current.send({
        action: 'typing',
        data: {
          conversationId,
          isTyping,
        },
      });
    }
  }, []);

  /**
   * Mark messages as read
   */
  const markAsRead = useCallback((conversationId: string) => {
    if (wsRef.current?.isConnected()) {
      wsRef.current.send({
        action: 'markAsRead',
        data: { conversationId },
      });
    }
  }, []);

  /**
   * Join a conversation room (for targeted messages)
   */
  const joinRoom = useCallback((conversationId: string) => {
    if (wsRef.current?.isConnected()) {
      wsRef.current.send({
        action: 'joinRoom',
        data: { conversationId },
      });
    }
  }, []);

  /**
   * Leave a conversation room
   */
  const leaveRoom = useCallback((conversationId: string) => {
    if (wsRef.current?.isConnected()) {
      wsRef.current.send({
        action: 'leaveRoom',
        data: { conversationId },
      });
    }
  }, []);

  /**
   * Connect/disconnect based on user session
   */
  useEffect(() => {
    if (user && token) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Join/leave room when conversationId changes
  useEffect(() => {
    if (options.conversationId && isConnected) {
      joinRoom(options.conversationId);
      return () => leaveRoom(options.conversationId!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.conversationId, isConnected]);

  // Helper function to check if a user is online
  const isUserOnline = useCallback((userId: string) => {
    return onlineUsers.has(userId);
  }, [onlineUsers]);

  return {
    isConnected,
    status,
    isUserTyping,
    onlineUsers,
    isUserOnline,
    reconnect: connect,
    sendTyping,
    markAsRead,
    joinRoom,
    leaveRoom,
  };
}
